import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Save, Loader2, Plus, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';

// Validation schema
const assetSchema = z.object({
  assetType: z.enum(['assignable', 'non_assignable']),
  assetId: z.string().max(100, 'Asset ID must be less than 100 characters').optional().or(z.literal('')),
  name: z.string().min(1, 'Name is required').max(100, 'Name must be less than 100 characters'),
  assetTag: z.string().min(1, 'Asset tag is required').max(50, 'Asset tag must be less than 50 characters'),
  serialNumber: z.string().max(100, 'Serial number must be less than 100 characters').optional().or(z.literal('')),
  categoryId: z.string().min(1, 'Category is required'),
  brandId: z.string().optional().or(z.literal('')),
  vendorId: z.string().optional().or(z.literal('')),
  branchId: z.string().optional().or(z.literal('')),
  departmentId: z.string().optional().or(z.literal('')),
  employeeId: z.string().optional().or(z.literal('')),
  purchaseDate: z.string().optional().or(z.literal('')),
  purchaseCost: z.string().optional().or(z.literal('')),
  warrantyExpiry: z.string().optional().or(z.literal('')),
  status: z.enum(['AVAILABLE', 'ASSIGNED', 'DEPLOYED', 'IN_STOCK', 'IN_MAINTENANCE', 'UNDER_REPAIR', 'RETIRED', 'DISPOSED', 'LOST', 'RESERVED']),
  description: z.string().max(500, 'Description must be less than 500 characters').optional().or(z.literal('')),
  model: z.string().max(100, 'Model must be less than 100 characters').optional().or(z.literal('')),
  condition: z.enum(['NEW', 'GOOD', 'FAIR', 'POOR']).optional(),
});

type AssetFormData = z.infer<typeof assetSchema>;

interface SelectOption {
  id: string;
  name: string;
}

export default function AssetFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isEditMode = Boolean(id);
  
  // State for add model modal
  const [showAddModel, setShowAddModel] = useState(false);
  const [newModelName, setNewModelName] = useState('');
  const [isAddingModel, setIsAddingModel] = useState(false);
  
  // State for serial number validation
  const [serialNumberStatus, setSerialNumberStatus] = useState<'idle' | 'checking' | 'valid' | 'duplicate'>('idle');
  const [serialNumberError, setSerialNumberError] = useState<string | null>(null);
  const [duplicateAsset, setDuplicateAsset] = useState<{
    id: string;
    assetTag: string;
    name: string;
    serialNumber: string;
    model?: string;
    status: string;
    condition?: string;
    category: string;
    brand: string;
    branch: string;
    department: string;
    assignedTo: string | null;
  } | null>(null);

  // Form setup
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<AssetFormData>({
    resolver: zodResolver(assetSchema),
    defaultValues: {
      assetType: 'assignable',
      assetId: '',
      name: '',
      assetTag: '',
      serialNumber: '',
      categoryId: '',
      brandId: '',
      vendorId: '',
      branchId: '',
      departmentId: '',
      employeeId: '',
      purchaseDate: '',
      purchaseCost: '',
      warrantyExpiry: '',
      status: 'IN_STOCK',
      description: '',
      model: '',
      condition: undefined,
    },
  });

  // Watch asset type to toggle fields
  const assetType = watch('assetType');
  const serialNumber = watch('serialNumber');

  // Fetch next asset tag for new assets
  const { data: nextTagData } = useQuery({
    queryKey: ['nextAssetTag'],
    queryFn: async () => {
      const response = await api.get('/assets/next-tag?prefix=SAZ');
      return response.data.data;
    },
    enabled: !isEditMode,
  });

  // Set asset tag when fetched (for new assets)
  useEffect(() => {
    if (nextTagData?.assetTag && !isEditMode) {
      setValue('assetTag', nextTagData.assetTag);
    }
  }, [nextTagData, isEditMode, setValue]);

  // Fetch existing asset for edit mode
  const { data: asset, isLoading: assetLoading } = useQuery({
    queryKey: ['asset', id],
    queryFn: async () => {
      const response = await api.get(`/assets/${id}`);
      return response.data.data;
    },
    enabled: isEditMode,
  });

  // Debounced serial number validation (after asset query)
  useEffect(() => {
    if (!serialNumber || serialNumber.trim() === '') {
      setSerialNumberStatus('idle');
      setSerialNumberError(null);
      setDuplicateAsset(null);
      clearErrors('serialNumber');
      return;
    }

    // Don't validate if it's the same serial number in edit mode
    if (isEditMode && asset?.serialNumber === serialNumber) {
      setSerialNumberStatus('valid');
      setSerialNumberError(null);
      setDuplicateAsset(null);
      clearErrors('serialNumber');
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSerialNumberStatus('checking');
      try {
        const excludeId = isEditMode ? id : undefined;
        const url = `/assets/validate-serial/${encodeURIComponent(serialNumber)}${excludeId ? `?excludeAssetId=${excludeId}` : ''}`;
        const response = await api.get(url);
        if (response.data.data.exists) {
          setSerialNumberStatus('duplicate');
          setSerialNumberError('This serial number already exists in the system');
          setDuplicateAsset(response.data.data.duplicateAsset || null);
          setError('serialNumber', { type: 'manual', message: 'Serial number already exists' });
        } else {
          setSerialNumberStatus('valid');
          setSerialNumberError(null);
          setDuplicateAsset(null);
          clearErrors('serialNumber');
        }
      } catch (error) {
        setSerialNumberStatus('idle');
        setSerialNumberError(null);
        setDuplicateAsset(null);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [serialNumber, isEditMode, id, asset?.serialNumber, setError, clearErrors]);

  // Fetch dropdown options
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await api.get('/categories');
      return response.data.data || [];
    },
  });

  const { data: brands } = useQuery({
    queryKey: ['brands'],
    queryFn: async () => {
      const response = await api.get('/brands');
      return response.data.data || [];
    },
  });

  const { data: vendors } = useQuery({
    queryKey: ['vendors'],
    queryFn: async () => {
      const response = await api.get('/vendors');
      return response.data.data || [];
    },
  });

  const { data: branches } = useQuery({
    queryKey: ['branches'],
    queryFn: async () => {
      const response = await api.get('/branches');
      return response.data.data || [];
    },
  });

  // Fetch departments
  const { data: departments } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      const response = await api.get('/departments');
      return response.data.data || [];
    },
  });

  // Fetch distinct models for suggestions
  const selectedCategoryId = watch('categoryId');
  const selectedBrandId = watch('brandId');
  const currentModel = watch('model');
  const { data: models, refetch: refetchModels } = useQuery({
    queryKey: ['asset-models', selectedCategoryId, selectedBrandId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCategoryId) params.append('categoryId', selectedCategoryId);
      if (selectedBrandId) params.append('brandId', selectedBrandId);
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await api.get(`/assets/models${queryString}`);
      return response.data.data || [];
    },
  });
  
  // Ensure current model is in the list (for edit mode)
  const modelOptions = useMemo(() => {
    const modelList = models as Array<{ id?: string; name: string }> || [];
    // If current model exists and is not in the fetched list, add it
    if (currentModel && !modelList.some(m => m.name === currentModel)) {
      return [{ name: currentModel }, ...modelList];
    }
    return modelList;
  }, [models, currentModel]);

  // Add new model handler
  const handleAddModel = async () => {
    if (!newModelName.trim()) {
      toast({ title: 'Error', description: 'Model name is required', variant: 'destructive' });
      return;
    }
    setIsAddingModel(true);
    try {
      const response = await api.post('/assets/models', {
        name: newModelName.trim(),
        categoryId: selectedCategoryId || undefined,
        brandId: selectedBrandId || undefined,
      });
      if (response.data.success) {
        toast({ title: 'Success', description: 'Model added successfully' });
        setValue('model', newModelName.trim());
        setNewModelName('');
        setShowAddModel(false);
        refetchModels();
      }
    } catch (error: any) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.error || 'Failed to add model', 
        variant: 'destructive' 
      });
    } finally {
      setIsAddingModel(false);
    }
  };

  // Populate form for edit mode
  useEffect(() => {
    if (asset && isEditMode) {
      reset({
        assetType: asset.isAssignable === false ? 'non_assignable' : 'assignable',
        assetId: asset.assetId || '',
        name: asset.name || '',
        assetTag: asset.assetTag || '',
        serialNumber: asset.serialNumber || '',
        categoryId: asset.categoryId || asset.category?.id || '',
        brandId: asset.brandId || asset.brand?.id || '',
        vendorId: asset.vendorId || asset.vendor?.id || '',
        branchId: asset.branchId || asset.branch?.id || '',
        departmentId: asset.departmentId || asset.department?.id || '',
        employeeId: '',
        purchaseDate: asset.purchaseDate ? asset.purchaseDate.split('T')[0] : '',
        purchaseCost: asset.purchaseCost?.toString() || '',
        warrantyExpiry: asset.warrantyExpiry ? asset.warrantyExpiry.split('T')[0] : '',
        status: asset.status || 'IN_STOCK',
        description: asset.description || '',
        model: asset.model || '',
        condition: asset.condition || undefined,
      });
    }
  }, [asset, isEditMode, reset]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: AssetFormData) => {
      const payload = {
        ...data,
        isAssignable: data.assetType === 'assignable',
        assetId: data.assetId || undefined,
        name: data.name || undefined,
        purchaseCost: data.purchaseCost ? parseFloat(data.purchaseCost) : undefined,
        brandId: data.brandId || undefined,
        vendorId: data.vendorId || undefined,
        branchId: data.branchId || undefined,
        departmentId: data.departmentId || undefined,
        serialNumber: data.serialNumber || undefined,
        purchaseDate: data.purchaseDate || undefined,
        warrantyExpiry: data.warrantyExpiry || undefined,
        description: data.description || undefined,
        model: data.model || undefined,
        condition: data.condition || undefined,
      };
      // Remove assetType from payload as it's converted to isAssignable
      delete (payload as any).assetType;
      delete (payload as any).employeeId;
      const response = await api.post('/assets', payload);
      return response.data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['assets'], refetchType: 'all' });
      await queryClient.invalidateQueries({ queryKey: ['asset-statistics'], refetchType: 'all' });
      toast({
        title: 'Success',
        description: 'Asset created successfully',
      });
      navigate(`/assets/${data.data?.id || ''}`);
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to create asset',
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: AssetFormData) => {
      const payload = {
        ...data,
        isAssignable: data.assetType === 'assignable',
        assetId: data.assetId || undefined,
        name: data.name || undefined,
        purchaseCost: data.purchaseCost ? parseFloat(data.purchaseCost) : undefined,
        brandId: data.brandId || undefined,
        vendorId: data.vendorId || undefined,
        branchId: data.branchId || undefined,
        departmentId: data.departmentId || undefined,
        serialNumber: data.serialNumber || undefined,
        purchaseDate: data.purchaseDate || undefined,
        warrantyExpiry: data.warrantyExpiry || undefined,
        description: data.description || undefined,
        model: data.model || undefined,
        condition: data.condition || undefined,
      };
      // Remove assetType from payload as it's converted to isAssignable
      delete (payload as any).assetType;
      delete (payload as any).employeeId;
      const response = await api.patch(`/assets/${id}`, payload);
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['assets'], refetchType: 'all' });
      await queryClient.invalidateQueries({ queryKey: ['asset', id], refetchType: 'all' });
      await queryClient.invalidateQueries({ queryKey: ['asset-statistics'], refetchType: 'all' });
      toast({
        title: 'Success',
        description: 'Asset updated successfully',
      });
      navigate(`/assets/${id}`);
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to update asset',
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data: AssetFormData) => {
    // Prevent submission if serial number is duplicate
    if (serialNumberStatus === 'duplicate') {
      toast({
        title: 'Error',
        description: 'Cannot save asset with duplicate serial number',
        variant: 'destructive',
      });
      return;
    }
    
    if (isEditMode) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  if (isEditMode && assetLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={isEditMode ? `/assets/${id}` : '/assets'}>
            <ArrowLeft className="w-5 h-5" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold">{isEditMode ? 'Edit Asset' : 'Add New Asset'}</h1>
          <p className="text-muted-foreground">
            {isEditMode ? 'Update asset information' : 'Create a new asset in the system'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Asset Type Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Asset Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4">
                  <label
                    className={cn(
                      "flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all",
                      assetType === 'assignable' 
                        ? "border-primary bg-primary/5" 
                        : "border-input hover:border-primary/50"
                    )}
                  >
                    <input
                      type="radio"
                      {...register('assetType')}
                      value="assignable"
                      className="sr-only"
                    />
                    <div className="text-2xl">📱</div>
                    <div>
                      <p className="font-medium">Assignable</p>
                      <p className="text-sm text-muted-foreground">Issued to Employee (laptops, phones, etc.)</p>
                    </div>
                  </label>
                  <label
                    className={cn(
                      "flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all",
                      assetType === 'non_assignable' 
                        ? "border-primary bg-primary/5" 
                        : "border-input hover:border-primary/50"
                    )}
                  >
                    <input
                      type="radio"
                      {...register('assetType')}
                      value="non_assignable"
                      className="sr-only"
                    />
                    <div className="text-2xl">🏢</div>
                    <div>
                      <p className="font-medium">Non-Assignable</p>
                      <p className="text-sm text-muted-foreground">Branch/Department Asset (servers, printers, etc.)</p>
                    </div>
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="assetId">Asset ID</Label>
                    <Input
                      id="assetId"
                      {...register('assetId')}
                      error={errors.assetId?.message}
                      placeholder="e.g., L-UGSL0100, P-HQ002"
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Custom identifier (employee ID prefix, location code, etc.)
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="assetTag">Asset Tag *</Label>
                    <Input
                      id="assetTag"
                      {...register('assetTag')}
                      error={errors.assetTag?.message}
                      placeholder="Auto-generated..."
                      className="mt-1 bg-muted cursor-not-allowed"
                      readOnly
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {isEditMode ? 'Asset tag cannot be changed' : 'Auto-generated based on SAZ prefix'}
                    </p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">
                      Name / Description *
                    </Label>
                    <Input
                      id="name"
                      {...register('name')}
                      error={errors.name?.message}
                      placeholder="e.g., HP ProBook 430 G8"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="serialNumber">Serial Number</Label>
                    <div className="relative mt-1">
                      <Input
                        id="serialNumber"
                        {...register('serialNumber')}
                        error={errors.serialNumber?.message || serialNumberError || undefined}
                        placeholder="e.g., 5CD1234567"
                        className={cn(
                          serialNumberStatus === 'duplicate' && 'border-destructive focus-visible:ring-destructive',
                          serialNumberStatus === 'valid' && 'border-green-500 focus-visible:ring-green-500'
                        )}
                      />
                      {serialNumberStatus === 'checking' && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                        </div>
                      )}
                      {serialNumberStatus === 'valid' && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        </div>
                      )}
                      {serialNumberStatus === 'duplicate' && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <AlertCircle className="w-4 h-4 text-destructive" />
                        </div>
                      )}
                    </div>
                    {serialNumberError && (
                      <p className="mt-1 text-sm text-destructive">{serialNumberError}</p>
                    )}
                    {/* Duplicate Asset Details */}
                    {duplicateAsset && serialNumberStatus === 'duplicate' && (
                      <div className="mt-3 p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
                        <div className="flex items-center gap-2 mb-3">
                          <AlertCircle className="w-5 h-5 text-destructive" />
                          <h4 className="font-semibold text-destructive">Duplicate Serial Number Found</h4>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">Asset Tag:</span>
                            <Link 
                              to={`/assets/${duplicateAsset.id}`} 
                              className="ml-2 font-medium text-primary hover:underline"
                            >
                              {duplicateAsset.assetTag}
                            </Link>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Name:</span>
                            <span className="ml-2 font-medium">{duplicateAsset.name || '-'}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Model:</span>
                            <span className="ml-2 font-medium">{duplicateAsset.model || '-'}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Category:</span>
                            <span className="ml-2 font-medium">{duplicateAsset.category}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Brand:</span>
                            <span className="ml-2 font-medium">{duplicateAsset.brand}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Status:</span>
                            <span className={cn(
                              "ml-2 px-2 py-0.5 rounded text-xs font-medium",
                              duplicateAsset.status === 'AVAILABLE' && "bg-green-100 text-green-800",
                              duplicateAsset.status === 'ASSIGNED' && "bg-blue-100 text-blue-800",
                              duplicateAsset.status === 'IN_MAINTENANCE' && "bg-yellow-100 text-yellow-800",
                              duplicateAsset.status === 'RETIRED' && "bg-gray-100 text-gray-800"
                            )}>
                              {duplicateAsset.status}
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Branch:</span>
                            <span className="ml-2 font-medium">{duplicateAsset.branch}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Department:</span>
                            <span className="ml-2 font-medium">{duplicateAsset.department}</span>
                          </div>
                          {duplicateAsset.assignedTo && (
                            <div className="col-span-2">
                              <span className="text-muted-foreground">Assigned To:</span>
                              <span className="ml-2 font-medium">{duplicateAsset.assignedTo}</span>
                            </div>
                          )}
                        </div>
                        <div className="mt-3 pt-3 border-t border-destructive/20">
                          <Link 
                            to={`/assets/${duplicateAsset.id}`}
                            className="text-sm text-primary hover:underline"
                          >
                            View Asset Details →
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="model">Model</Label>
                    <div className="flex gap-2 mt-1">
                      <select
                        id="model"
                        {...register('model')}
                        className={cn(
                          "flex-1 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm",
                          errors.model && "border-destructive"
                        )}
                      >
                        <option value="">Select a model...</option>
                        {modelOptions.map((model) => (
                          <option key={model.id || model.name} value={model.name}>
                            {model.name}
                          </option>
                        ))}
                      </select>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setShowAddModel(true)}
                        title="Add new model"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {errors.model && (
                      <p className="mt-1 text-sm text-destructive">{errors.model.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <textarea
                    id="description"
                    {...register('description')}
                    className="w-full min-h-[100px] px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
                    placeholder="Enter asset description..."
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-destructive">{errors.description.message}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Classification */}
            <Card>
              <CardHeader>
                <CardTitle>Classification</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="categoryId">Category *</Label>
                    <select
                      id="categoryId"
                      {...register('categoryId')}
                      className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                    >
                      <option value="">Select category...</option>
                      {(categories as SelectOption[] || []).map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                    {errors.categoryId && (
                      <p className="mt-1 text-sm text-destructive">{errors.categoryId.message}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="brandId">Brand</Label>
                    <select
                      id="brandId"
                      {...register('brandId')}
                      className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                    >
                      <option value="">Select brand...</option>
                      {(brands as SelectOption[] || []).map((brand) => (
                        <option key={brand.id} value={brand.id}>{brand.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="vendorId">Vendor</Label>
                    <select
                      id="vendorId"
                      {...register('vendorId')}
                      className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                    >
                      <option value="">Select vendor...</option>
                      {(vendors as SelectOption[] || []).map((vendor) => (
                        <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Location - Required for non-assignable, optional for assignable */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="branchId">
                      Branch {assetType === 'non_assignable' && '*'}
                    </Label>
                    <select
                      id="branchId"
                      {...register('branchId')}
                      className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                    >
                      <option value="">Select branch...</option>
                      {(branches as SelectOption[] || []).map((branch) => (
                        <option key={branch.id} value={branch.id}>{branch.name}</option>
                      ))}
                    </select>
                    {assetType === 'assignable' && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Optional - will be set when assigned to employee
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="departmentId">
                      Department {assetType === 'non_assignable' && '*'}
                    </Label>
                    <select
                      id="departmentId"
                      {...register('departmentId')}
                      className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                    >
                      <option value="">Select department...</option>
                      {(departments as SelectOption[] || []).map((dept) => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </select>
                    {assetType === 'assignable' && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Optional - will be set when assigned to employee
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Financial Information */}
            <Card>
              <CardHeader>
                <CardTitle>Financial Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="purchaseDate">Purchase Date</Label>
                    <Input
                      id="purchaseDate"
                      type="date"
                      {...register('purchaseDate')}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="purchaseCost">Purchase Cost ($)</Label>
                    <Input
                      id="purchaseCost"
                      type="number"
                      step="0.01"
                      min="0"
                      {...register('purchaseCost')}
                      error={errors.purchaseCost?.message}
                      placeholder="0.00"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="warrantyExpiry">Warranty Expiry</Label>
                    <Input
                      id="warrantyExpiry"
                      type="date"
                      {...register('warrantyExpiry')}
                      className="mt-1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status & Condition */}
            <Card>
              <CardHeader>
                <CardTitle>Status & Condition</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="status">Status *</Label>
                  <select
                    id="status"
                    {...register('status')}
                    className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="AVAILABLE">Available</option>
                    <option value="ASSIGNED">Assigned</option>
                    <option value="DEPLOYED">Deployed</option>
                    <option value="IN_STOCK">In Stock</option>
                    <option value="IN_MAINTENANCE">In Maintenance</option>
                    <option value="UNDER_REPAIR">Under Repair</option>
                    <option value="RETIRED">Retired</option>
                    <option value="DISPOSED">Disposed</option>
                    <option value="LOST">Lost</option>
                    <option value="RESERVED">Reserved</option>
                  </select>
                  {errors.status && (
                    <p className="mt-1 text-sm text-destructive">{errors.status.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="condition">Condition</Label>
                  <select
                    id="condition"
                    {...register('condition')}
                    className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">Select condition...</option>
                    <option value="NEW">New</option>
                    <option value="GOOD">Good</option>
                    <option value="FAIR">Fair</option>
                    <option value="POOR">Poor</option>
                  </select>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <Button type="submit" className="w-full" disabled={isPending}>
                    {isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {isEditMode ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {isEditMode ? 'Update Asset' : 'Create Asset'}
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate(isEditMode ? `/assets/${id}` : '/assets')}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Help Text */}
            <Card>
              <CardContent className="p-6">
                <h4 className="font-medium mb-2">Tips</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Asset tags should be unique</li>
                  <li>• Serial numbers help with warranty claims</li>
                  <li>• Set warranty expiry for reminders</li>
                  <li>• Categories help organize assets</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>

      {/* Add Model Modal */}
      {showAddModel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Add New Model</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="newModelName">Model Name *</Label>
                <Input
                  id="newModelName"
                  value={newModelName}
                  onChange={(e) => setNewModelName(e.target.value)}
                  placeholder="e.g., ProBook 430 G8"
                  className="mt-1"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddModel();
                    }
                  }}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddModel(false);
                    setNewModelName('');
                  }}
                  disabled={isAddingModel}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleAddModel}
                  disabled={isAddingModel || !newModelName.trim()}
                >
                  {isAddingModel ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Add Model
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
